import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DuplicateSettingsRequest } from 'api/services/NodeSettings';
import { useEffect, useMemo, useState } from 'react';
import { toTitle, vendorSettingsProperties } from './generateUiSchema';

type ProcessNode = {
  children: ProcessNode[];
  label: string;
  path: string;
  selectable: boolean;
};

type VendorProcessProperties = Record<string, { process?: string }>;

type SettingsSchema = {
  properties?: {
    vendorsettings?: {
      properties?: VendorProcessProperties;
      allOf?: { properties?: VendorProcessProperties }[];
    };
  };
};

type Props = {
  isPending: boolean;
  onClose: () => void;
  onImport: (request: DuplicateSettingsRequest) => void;
  open: boolean;
  schema?: SettingsSchema;
};

// Insertion order is the schema order, which is also the tab order of the main form.
export const buildProcessTree = (processes: string[]): ProcessNode[] => {
  const roots: ProcessNode[] = [];

  processes.forEach((process) => {
    let siblings = roots;
    let path = '';

    process.split('/').forEach((segment, index, segments) => {
      path = path ? `${path}/${segment}` : segment;
      // ponytail: process schemas are small; linear sibling lookup keeps the tree code simpler than nested maps.
      let node = siblings.find((candidate) => candidate.path === path);
      if (!node) {
        node = { children: [], label: toTitle(path), path, selectable: false };
        siblings.push(node);
      }
      if (index === segments.length - 1) node.selectable = true;
      siblings = node.children;
    });
  });

  return roots;
};

// Only real processes can be sent to the backend; group rows just toggle their descendants.
const selectablePaths = (node: ProcessNode): string[] => [
  ...(node.selectable ? [node.path] : []),
  ...node.children.flatMap(selectablePaths),
];

function ProcessBranch({
  depth = 0,
  nodes,
  selected,
  toggle,
}: {
  depth?: number;
  nodes: ProcessNode[];
  selected: Set<string>;
  toggle: (node: ProcessNode, checked: boolean) => void;
}) {
  return (
    <Box
      component={'ul'}
      sx={{
        listStyle: 'none',
        margin: 0,
        ...(depth > 0 && { borderColor: 'divider', borderLeft: 1, marginLeft: 2, paddingLeft: 1 }),
      }}
    >
      {nodes.map((node) => {
        const paths = selectablePaths(node);
        const checkedCount = paths.filter((path) => selected.has(path)).length;

        return (
          <Box component={'li'} key={node.path}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedCount === paths.length}
                  indeterminate={checkedCount > 0 && checkedCount < paths.length}
                  onChange={(_, checked) => toggle(node, checked)}
                  size={'small'}
                />
              }
              label={node.label}
              sx={{ '& .MuiFormControlLabel-label': { fontWeight: node.children.length > 0 ? 500 : 400 } }}
            />
            {node.children.length > 0 && (
              <ProcessBranch depth={depth + 1} nodes={node.children} selected={selected} toggle={toggle} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function SettingsImportDialog({ isPending, onClose, onImport, open, schema }: Props) {
  const [sourceClientId, setSourceClientId] = useState('');
  const [sourceNodeId, setSourceNodeId] = useState('');
  const [filterSettings, setFilterSettings] = useState(false);
  const [selectedProcesses, setSelectedProcesses] = useState<Set<string>>(new Set());

  const processTree = useMemo(() => {
    const processDefinitions = vendorSettingsProperties(schema);
    const processes = Array.from(new Set(Object.values(processDefinitions).flatMap(({ process }) => (process ? [process] : []))));
    return buildProcessTree(processes);
  }, [schema]);

  useEffect(() => {
    if (open) return;
    setSourceClientId('');
    setSourceNodeId('');
    setFilterSettings(false);
    setSelectedProcesses(new Set());
  }, [open]);

  const toggleProcess = (node: ProcessNode, checked: boolean) => {
    setSelectedProcesses((current) => {
      const updated = new Set(current);
      selectablePaths(node).forEach((process) => (checked ? updated.add(process) : updated.delete(process)));
      if (!checked) {
        // The backend also copies descendants of a selected process, so drop partially selected ancestors.
        current.forEach((process) => {
          if (node.path.startsWith(`${process}/`)) updated.delete(process);
        });
      }
      return updated;
    });
  };

  const sourceClient = Number(sourceClientId);
  const canImport =
    Number.isInteger(sourceClient) &&
    sourceClient > 0 &&
    sourceNodeId.trim().length > 0 &&
    (!filterSettings || selectedProcesses.size > 0);

  const handleImport = () => {
    if (!canImport) return;
    onImport({
      source_client_id: sourceClient,
      source_node_id: sourceNodeId.trim(),
      ...(filterSettings ? { processes: Array.from(selectedProcesses).sort() } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={'sm'}>
      <DialogTitle>Import settings from another asset</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, marginTop: 1 }}>
          <TextField
            fullWidth
            inputProps={{ min: 1 }}
            label={'Source client ID'}
            onChange={(event) => setSourceClientId(event.target.value)}
            type={'number'}
            value={sourceClientId}
          />
          <TextField
            fullWidth
            label={'Source asset ID'}
            onChange={(event) => setSourceNodeId(event.target.value)}
            value={sourceNodeId}
          />
        </Box>
        <FormControlLabel
          control={<Checkbox checked={filterSettings} onChange={(_, checked) => setFilterSettings(checked)} />}
          label={'Filter settings to copy'}
        />
        {filterSettings && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 360, overflowY: 'auto', padding: 1 }}>
            {processTree.length > 0 ? (
              <ProcessBranch nodes={processTree} selected={selectedProcesses} toggle={toggleProcess} />
            ) : (
              <Typography color={'text.secondary'}>No processes available.</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={!canImport || isPending} onClick={handleImport} variant={'contained'}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
